import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './HomePage.css';

// =====================================================
// ✅ BONUS RULES — must match backend config
// =====================================================
const BONUS_RULES = {
  minSelections: 7,
  minOddsPerSelection: 1.60,
  maxStakePerBet: 20,
};

const HomePage = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedSport, setSelectedSport] = useState('FOOTBALL');
  const [betSlip, setBetSlip] = useState([]);
  const [balance, setBalance] = useState(0);
  const [bonusBalance, setBonusBalance] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showBetSlip, setShowBetSlip] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedLeague, setSelectedLeague] = useState('');
  const [leagues, setLeagues] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [totalStake, setTotalStake] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [showTelegramPopup, setShowTelegramPopup] = useState(false);
  const dropdownRef = useRef(null);

  // ✅ BONUS RULES MODAL STATE
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulesToShow, setRulesToShow] = useState([]);

  // ✅ DUPLICATE PROTECTION STATE
  const placingBetRef = useRef(false);
  const lastBetTimeRef = useRef(0);
  const [placingBet, setPlacingBet] = useState(false);
  const [pendingMatchIds, setPendingMatchIds] = useState([]);

  // ===== CAROUSEL STATE (10 images, 2 per slide = 5 slides) =====
  const [currentSlide, setCurrentSlide] = useState(0);
  // ===== PROMOTIONAL BANNERS =====
  const bannerImages = [
    { src: '/assets/banner-live-betting.jpeg', alt: 'Live Betting' },
    { src: '/assets/banner-cashback.jpeg', alt: 'Cashback 1' },
    { src: '/assets/banner-sponsors.jpeg', alt: 'Sponsors 1' },
    { src: '/assets/promo-image.jpeg', alt: 'Promotion 1' },
    { src: '/assets/gem1.jpg', alt: 'Gemini' },
    { src: '/assets/Female_promotor.JPEG', alt: 'Female Promoter' },
    { src: '/assets/Famous_five_players.JPEG', alt: 'Famous Five Players' },
    { src: '/assets/banner-sponsors-sec.jpg', alt: 'Sponsors Sec' },
    { src: '/assets/gem1.jpg', alt: 'Gemini' },
    // Rest of the duplicates for the carousel...
    { src: '/assets/banner-cashback.jpeg', alt: 'Cashback 2' },
    { src: '/assets/banner-sponsors.jpeg', alt: 'Sponsors 2' },
    { src: '/assets/promo-image.jpeg', alt: 'Promotion 2' },
    { src: '/assets/gem1.jpg', alt: 'Gemini' },
  ];
  const slidesPerView = 2;
  const totalSlides = Math.ceil(bannerImages.length / slidesPerView);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // ✅ Sports with Aviator navigation
  const sports = [
    { id: 'FOOTBALL', name: 'Football', icon: '⚽' },
    { id: 'BASKETBALL', name: 'Basketball', icon: '🏀' },
    { id: 'AVIATOR', name: 'Aviator', icon: '✈️', path: '/Aviator' },
    { id: 'FAST_KENO', name: 'Fast Keno', icon: '🎲', comingSoon: true }
  ];

  const comingSoonSports = ['FAST_KENO'];

  // ✅ MASTER LIST OF ALL LEAGUES (Always Visible)
  const MASTER_LEAGUES = [
    'Premier League',
    'LaLiga',
    'Serie A',
    'Bundesliga',
    'Ligue 1',
    'UEFA Champions League',
    'UEFA Europa League',
    'UEFA Conference League',
    'Eredivisie',
    'Liga Portugal',
    'Belgian Pro League',
    'Super League',
    'Superettan'
  ];

  // ✅ All markets mapping (FULL - 82+ entries)
  const allMarkets = {
    result: { label: 'Result', icon: '🏆' },
    doubleChance: { label: 'Double Chance', icon: '🔄' },
    drawNoBet: { label: 'Draw No Bet', icon: '⏳' },
    btts: { label: 'Both Teams to Score', icon: '⚽' },
    totalGoals: { label: 'Total Goals', icon: '📊' },
    exactGoals: { label: 'Exact Goals', icon: '🎯' },
    correctScore: { label: 'Correct Score', icon: '📝' },
    halfTimeResult: { label: 'Half-Time Result', icon: '⏰' },
    firstHalfResult: { label: '1st Half – 3 Way', icon: '⏰' },
    halfTimeFullTime: { label: 'HT/FT', icon: '🔄' },
    firstHalfCorrectScore: { label: '1st Half – Correct Score', icon: '📝' },
    oddEven: { label: 'Odd/Even', icon: '🔢' },
    firstHalfOddEven: { label: '1st Half – Odd/Even', icon: '🔢' },
    secondHalfOddEven: { label: '2nd Half – Odd/Even', icon: '🔢' },
    homeOddEven: { label: 'Home Odd/Even', icon: '🔢' },
    awayOddEven: { label: 'Away Odd/Even', icon: '🔢' },
    firstTeamScore: { label: 'First Team to Score', icon: '🥇' },
    lastTeamScore: { label: 'Last Team to Score', icon: '🥈' },
    firstGoalTime: { label: 'First Goal Time', icon: '⏱️' },
    teamGoalsHome: { label: 'Home Team Goals', icon: '🏠' },
    teamGoalsAway: { label: 'Away Team Goals', icon: '✈️' },
    handicap: { label: 'Handicap', icon: '📈' },
    asianHandicap: { label: 'Asian Handicap', icon: '🌏' },
    corners: { label: 'Corners', icon: '🔄' },
    cards: { label: 'Cards', icon: '🟨' },
    penalty: { label: 'Penalty', icon: '⚪' },
    playerMarkets: { label: 'Player Markets', icon: '👤' },
    specials: { label: 'Specials', icon: '⭐' },
    firstHalfTotalGoals: { label: '1st Half – Total Goals', icon: '📊' },
    firstHalfBtts: { label: '1st Half – BTTS', icon: '⚽' },
    secondHalfResult: { label: '2nd Half – 3 Way', icon: '⏰' },
    secondHalfBtts: { label: '2nd Half – BTTS', icon: '⚽' },
    secondHalfTotalGoals: { label: '2nd Half – Total Goals', icon: '📊' },
    homeCleanSheet: { label: 'Home Clean Sheet', icon: '🧤' },
    awayCleanSheet: { label: 'Away Clean Sheet', icon: '🧤' },
    firstHalfHomeCleanSheet: { label: '1st Half – Home Clean Sheet', icon: '🧤' },
    firstHalfAwayCleanSheet: { label: '1st Half – Away Clean Sheet', icon: '🧤' },
    secondHalfHomeCleanSheet: { label: '2nd Half – Home Clean Sheet', icon: '🧤' },
    secondHalfAwayCleanSheet: { label: '2nd Half – Away Clean Sheet', icon: '🧤' },
    homeOverUnder: { label: 'Home Team – Over/Under', icon: '📊' },
    awayTotal: { label: 'Away Team – Over/Under', icon: '📊' },
    firstHalfHomeOverUnder: { label: '1st Half – Home Over/Under', icon: '📊' },
    firstHalfAwayOverUnder: { label: '1st Half – Away Over/Under', icon: '📊' },
    secondHalfHomeOverUnder: { label: '2nd Half – Home Over/Under', icon: '📊' },
    secondHalfAwayOverUnder: { label: '2nd Half – Away Over/Under', icon: '📊' },
    homeExactGoals: { label: 'Home – Exact Goals', icon: '🎯' },
    awayExactGoals: { label: 'Away – Exact Goals', icon: '🎯' },
    goalRange: { label: 'Goal Range', icon: '📊' },
    threeWayOverUnder: { label: '3 Way + Over/Under', icon: '📊' },
    threeWayBtts: { label: '3 Way + BTTS', icon: '⚽' },
    doubleChanceBtts: { label: 'Double Chance + BTTS', icon: '🔄' },
    doubleChanceOverUnder: { label: 'Double Chance + Over/Under', icon: '🔄' },
    overUnderBtts: { label: 'Over/Under + BTTS', icon: '📊' },
    whichTeamToScore: { label: 'Which Team to Score', icon: '⚽' },
    oneGoal: { label: '1 Goal Markets', icon: '1️⃣' },
    oneGoalAnd1x2: { label: '1 Goal + 1X2', icon: '1️⃣' },
    firstHalfOneGoal: { label: '1st Half – 1 Goal', icon: '1️⃣' },
    secondHalfOneGoal: { label: '2nd Half – 1 Goal', icon: '1️⃣' },
    lastGoal: { label: 'Last Goal', icon: '⚽' },
    tenMinute3Way: { label: '10 Min – 3 Way', icon: '⏱️' },
    firstHalfHandicap: { label: '1st Half – Handicap', icon: '📈' },
    secondHalfHandicap: { label: '2nd Half – Handicap', icon: '📈' },
    firstHalfDoubleChance: { label: '1st Half – Double Chance', icon: '🔄' },
    secondHalfDoubleChance: { label: '2nd Half – Double Chance', icon: '🔄' },
    firstHalfDrawNoBet: { label: '1st Half – Draw No Bet', icon: '⏳' },
    secondHalfDrawNoBet: { label: '2nd Half – Draw No Bet', icon: '⏳' },
    homeWinBothHalves: { label: 'Home Win Both Halves', icon: '🏆' },
    awayWinBothHalves: { label: 'Away Win Both Halves', icon: '🏆' },
    homeScoreBothHalves: { label: 'Home Score Both Halves', icon: '⚽' },
    awayScoreBothHalves: { label: 'Away Score Both Halves', icon: '⚽' },
    homeWinEitherHalf: { label: 'Home Win Either Half', icon: '🏆' },
    awayWinEitherHalf: { label: 'Away Win Either Half', icon: '🏆' },
    highestScoringHalf: { label: 'Highest Scoring Half', icon: '📊' },
    homeHighestScoringHalf: { label: 'Home – Highest Scoring Half', icon: '📊' },
    awayHighestScoringHalf: { label: 'Away – Highest Scoring Half', icon: '📊' },
    bothHalvesBtts: { label: 'Both Halves – BTTS', icon: '⚽' },
    bothHalvesOver1_5: { label: 'Both Halves – Over 1.5', icon: '📊' },
    bothHalvesUnder1_5: { label: 'Both Halves – Under 1.5', icon: '📊' },
    htFtFirstHalfOverUnder: { label: 'HT/FT + 1st Half O/U', icon: '🔄' },
    htFtExactGoals: { label: 'HT/FT + Exact Goals', icon: '🔄' },
    htFtOverUnder: { label: 'HT/FT + Over/Under', icon: '🔄' },
    htFtCorrectScore: { label: 'HT/FT + Correct Score', icon: '🔄' },
    firstHalf1x2Btts: { label: '1st Half – 1X2 + BTTS', icon: '⚽' },
    firstHalf1x2OverUnder: { label: '1st Half – 1X2 + O/U', icon: '📊' },
    doubleChanceFirstHalfBtts: { label: 'DC + 1st Half BTTS', icon: '🔄' },
    doubleChanceSecondHalfBtts: { label: 'DC + 2nd Half BTTS', icon: '🔄' },
    secondHalf3WayBtts: { label: '2nd Half – 3 Way + BTTS', icon: '⚽' },
    secondHalf3WayOverUnder: { label: '2nd Half – 3 Way + O/U', icon: '📊' },
    secondHalfCorrectScore: { label: '2nd Half – Correct Score', icon: '📝' },
    secondHalfExactGoals: { label: '2nd Half – Exact Goals', icon: '🎯' },
    secondHalfOverUnder: { label: '2nd Half – Over/Under', icon: '📊' },
    secondHalfDoubleChanceBtts: { label: '2nd Half – DC + BTTS', icon: '🔄' },
  };

  // ✅ 12-HOUR TIME FORMAT WITH AM/PM
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    
    const localDate = new Date(year, month, day, hours, minutes);
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const matchDate = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate());
    
    const timeStr = String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ' ' + ampm;
    
    if (matchDate.getTime() === today.getTime()) {
      return `Today, ${timeStr}`;
    } else if (matchDate.getTime() === tomorrow.getTime()) {
      return `Tomorrow, ${timeStr}`;
    } else {
      const dateStr = String(month + 1).padStart(2, '0') + '/' + String(day).padStart(2, '0') + '/' + String(year);
      return `${dateStr}, ${timeStr}`;
    }
  };

  // ✅ Check if match has started
  const hasMatchStarted = (dateString) => {
    if (!dateString) return true;
    const matchDate = new Date(dateString);
    const now = new Date();
    return matchDate <= now;
  };

  // ✅ Get time left with 12-hour calculation
  const getTimeLeft = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const matchDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    );
    
    const now = new Date();
    const diffMs = matchDate - now;
    
    if (diffMs < 0) return 'Started';
    
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours % 24}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMins % 60}m`;
    } else {
      return `${diffMins}m`;
    }
  };

  // Get day name
  const getDayName = (dateString) => {
    if (!dateString) return 'Unknown';
    
    const date = new Date(dateString);
    const localDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const diffDays = Math.floor((localDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === 2) return 'In 2 Days';
    if (diffDays === -1) return 'Yesterday';
    
    return localDate.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  // Sort matches by date
  const sortMatchesByDate = (matchesArray) => {
    return [...matchesArray].sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });
  };

  // Group matches by day
  const groupMatchesByDay = (matchesArray) => {
    const groups = {};
    matchesArray.forEach(match => {
      const dayKey = getDayName(match.date);
      if (!groups[dayKey]) {
        groups[dayKey] = [];
      }
      groups[dayKey].push(match);
    });
    return groups;
  };

  // Generate random 10-digit ticket ID
  const generateTicketId = () => {
    return Math.floor(1000000000 + Math.random() * 9000000000).toString();
  };

  // Print ticket function
  const printTicket = (betData) => {
    const ticketId = betData.ticketId || generateTicketId();
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Betting Ticket</title>
        <style>
          @page {
            size: 80mm 100mm;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            background: white;
            padding: 8px;
            width: 80mm;
            min-height: 100mm;
          }
          .ticket {
            width: 100%;
            border: 2px dashed #333;
            padding: 8px;
            background: white;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 6px;
            margin-bottom: 6px;
          }
          .header h2 {
            font-size: 14px;
            font-weight: bold;
            margin: 0;
            color: #1a1a2e;
          }
          .header .sub {
            font-size: 9px;
            color: #666;
          }
          .ticket-id {
            text-align: center;
            font-size: 12px;
            font-weight: bold;
            background: #f0f0f0;
            padding: 4px;
            margin-bottom: 6px;
            border-radius: 4px;
          }
          .ticket-id span {
            color: #6c5ce7;
          }
          .match-item {
            border-bottom: 1px dotted #ccc;
            padding: 4px 0;
            font-size: 10px;
          }
          .match-item .teams {
            font-weight: bold;
            font-size: 11px;
          }
          .match-item .detail {
            color: #555;
            font-size: 9px;
          }
          .match-item .status {
            float: right;
            font-weight: bold;
          }
          .status-pending { color: #f59f00; }
          .summary {
            margin-top: 6px;
            padding-top: 6px;
            border-top: 2px solid #333;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            padding: 2px 0;
          }
          .summary-row .label {
            color: #666;
          }
          .summary-row .value {
            font-weight: bold;
          }
          .total-row {
            border-top: 2px solid #333;
            margin-top: 4px;
            padding-top: 4px;
            font-size: 12px;
          }
          .total-row .value {
            color: #2ea043;
            font-size: 14px;
          }
          .footer {
            text-align: center;
            font-size: 8px;
            color: #999;
            margin-top: 6px;
            padding-top: 4px;
            border-top: 1px dotted #ccc;
          }
          .date-time {
            font-size: 8px;
            color: #999;
            text-align: center;
            margin-top: 4px;
          }
          @media print {
            body { margin: 0; padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="ticket">
          <div class="header">
            <h2>⚡ AsharaBet</h2>
            <div class="sub">Betting Ticket</div>
          </div>
          
          <div class="ticket-id">
            🎫 Ticket #<span>${ticketId}</span>
          </div>
          
          <div class="date-time">
            ${new Date().toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </div>

          <div style="margin: 6px 0;">
            ${betData.selections.map((sel, idx) => `
              <div class="match-item">
                <div class="teams">${idx + 1}. ${sel.match}</div>
                <div class="detail">
                  ${sel.market}: ${sel.betType} ${sel.odds}
                  <span class="status status-pending">⏳ PENDING</span>
                </div>
              </div>
            `).join('')}
          </div>

          <div class="summary">
            <div class="summary-row">
              <span class="label">Total Selections</span>
              <span class="value">${betData.selections.length}</span>
            </div>
            <div class="summary-row">
              <span class="label">Total Stake</span>
              <span class="value">ETB ${betData.totalStake.toFixed(2)}</span>
            </div>
            <div class="summary-row">
              <span class="label">Total Odds</span>
              <span class="value">${betData.totalOdds}</span>
            </div>
            <div class="total-row">
              <div class="summary-row">
                <span class="label" style="font-weight:bold;">Potential Win</span>
                <span class="value" style="color:#2ea043;font-size:16px;">
                  ETB ${betData.potentialWin.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <div class="footer">
            <div>🎯 Good Luck!</div>
            <div style="margin-top:2px;">Bet ID: ${ticketId}</div>
            <div style="margin-top:2px;">18+ | Play Responsibly</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=300,height=400');
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // ✅ Fetch balance — now also fetches bonusBalance
  const fetchBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API_URL}/api/user/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBalance(response.data.balance || 0);
        setBonusBalance(response.data.bonusBalance || 0);

        // ✅ Update local user object with wallet
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsed = JSON.parse(userData);
          parsed.wallet = {
            ...(parsed.wallet || {}),
            balance: response.data.balance || 0,
            bonusBalance: response.data.bonusBalance || 0,
          };
          localStorage.setItem('user', JSON.stringify(parsed));
          setUser(parsed);
        }
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  }, [API_URL]);

  // ✅ NEW: Fetch pending match IDs (for duplicate protection)
  const fetchPendingMatches = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_URL}/api/bets/pending-matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        console.log('📋 Pending match IDs:', res.data.matchIds);
        setPendingMatchIds(res.data.matchIds || []);
      }
    } catch (err) {
      console.error('Error fetching pending matches:', err);
    }
  }, [API_URL]);

  // Fetch matches
  const fetchMatches = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('sport', selectedSport);
      if (selectedLeague) params.append('league', selectedLeague);
      if (dateFilter !== 'all') params.append('date', dateFilter);

      const response = await axios.get(`${API_URL}/api/matches?${params}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      let matchesData = response.data.matches || [];
      
      // Filter: Remove matches that have already started
      const upcomingMatches = matchesData.filter(match => {
        if (match.status === 'LIVE' || match.status === 'live') return true;
        if (match.status === 'FINISHED' || match.status === 'finished') return false;
        return !hasMatchStarted(match.date);
      });
      
      matchesData = sortMatchesByDate(upcomingMatches);
      setMatches(matchesData);
      
      // ✅ KEEP THE DAY GROUPS
      const dayGroups = groupMatchesByDay(matchesData);
      
      const groupedByDay = Object.keys(dayGroups).map(day => ({
        day: day,
        matches: sortMatchesByDate(dayGroups[day])
      }));
      
      // Sort the day groups by their earliest match time
      groupedByDay.sort((a, b) => {
        const earliestA = new Date(a.matches[0]?.date || 0).getTime();
        const earliestB = new Date(b.matches[0]?.date || 0).getTime();
        return earliestA - earliestB;
      });
      
      setGroupedMatches(groupedByDay);
      
      // ✅ FIX: Only update the leagues list if we are viewing "All"
      if (!selectedLeague) {
        const fetchedLeagues = response.data.filters?.leagues || [];
        
        // ✅ Define your exact preferred order
        const preferredLeagueOrder = [
          'Premier League',
          'LaLiga',
          'Serie A',
          'Bundesliga',
          'Ligue 1',
          'UEFA Champions League',
          'UEFA Europa League',
          'UEFA Conference League',
          'Eredivisie',
          'Liga Portugal',
          'Belgian Pro League',
          'Super League'
        ];

        // ✅ Sort the leagues based on that order
        const sortedLeagues = [...fetchedLeagues].sort((a, b) => {
          const indexA = preferredLeagueOrder.indexOf(a);
          const indexB = preferredLeagueOrder.indexOf(b);

          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a.localeCompare(b);
        });

        setLeagues(sortedLeagues);
      }
      
      const live = matchesData.filter(m => m.status === 'LIVE' || m.status === 'live');
      setLiveMatches(live);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching matches:', error);
      setLoading(false);
    }
  }, [selectedSport, selectedLeague, dateFilter, API_URL]);

  // Handle resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ===== AUTO-SLIDE CAROUSEL (60 SECONDS) – 2 images per slide =====
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 60000);
    return () => clearInterval(interval);
  }, [totalSlides]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setBalance(parsedUser.wallet?.balance || 0);
        setBonusBalance(parsedUser.wallet?.bonusBalance || 0);
        fetchBalance();
        fetchPendingMatches(); // ✅ NEW
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    fetchMatches();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [fetchMatches, fetchBalance, fetchPendingMatches]);

  useEffect(() => {
    localStorage.setItem('betSlip', JSON.stringify(betSlip));
  }, [betSlip]);

  useEffect(() => {
    const savedBetSlip = localStorage.getItem('betSlip');
    if (savedBetSlip) {
      try {
        setBetSlip(JSON.parse(savedBetSlip));
      } catch (e) {}
    }
  }, []);

  // Add to bet slip
  const addToBetSlip = (match, betType, odds, market) => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (match.status === 'FINISHED' || match.status === 'finished') {
      alert('This match has already finished!');
      return;
    }

    if (hasMatchStarted(match.date)) {
      alert('This match has already started!');
      return;
    }

    // ✅ NEW: Block if user already has a pending bet on this match
    if (pendingMatchIds.includes(match._id.toString())) {
      alert(`⚠️ You already have a pending bet on this match.\n\nWait for it to settle before betting again.`);
      return;
    }

    const existingMatchIndex = betSlip.findIndex(b => b.matchId === match._id);

    if (existingMatchIndex !== -1) {
      const existingBet = betSlip[existingMatchIndex];
      if (existingBet.betType === betType && existingBet.market === market) {
        setBetSlip(betSlip.filter((_, index) => index !== existingMatchIndex));
      } else {
        const updated = [...betSlip];
        updated[existingMatchIndex] = {
          matchId: match._id,
          match: `${match.homeTeam} vs ${match.awayTeam}`,
          league: match.league || 'Unknown',
          date: match.date,
          betType,
          odds,
          market
        };
        setBetSlip(updated);
      }
    } else {
      setBetSlip([...betSlip, {
        matchId: match._id,
        match: `${match.homeTeam} vs ${match.awayTeam}`,
        league: match.league || 'Unknown',
        date: match.date,
        betType,
        odds,
        market
      }]);
    }
  };

  // Remove from bet slip
  const removeFromBetSlip = (index) => {
    setBetSlip(betSlip.filter((_, i) => i !== index));
  };

  // Update total stake
  const updateTotalStake = (value) => {
    setTotalStake(parseFloat(value) || 0);
  };

  // Calculate totals
  const calculateTotalOdds = () => {
    if (betSlip.length === 0) return 1;
    return betSlip.reduce((total, bet) => total * bet.odds, 1).toFixed(2);
  };

  const calculatePotentialWinnings = () => {
    const stake = parseFloat(totalStake) || 0;
    const odds = parseFloat(calculateTotalOdds());
    return (stake * odds).toFixed(2);
  };

  // =====================================================
  // ✅ BONUS RULES CHECKER (live, for UI preview)
  // =====================================================
  const checkBonusRules = () => {
    const rules = [];

    // 1. Minimum 7 selections
    const selectionsOk = betSlip.length >= BONUS_RULES.minSelections;
    rules.push({
      rule: `Minimum ${BONUS_RULES.minSelections} selections`,
      pass: selectionsOk,
      detail: `${betSlip.length}/${BONUS_RULES.minSelections}`
    });

    // 2. Each selection odds ≥ 1.60
    const badSelections = betSlip.filter(b => Number(b.odds) < BONUS_RULES.minOddsPerSelection);
    const oddsOk = badSelections.length === 0;
    rules.push({
      rule: `Each selection odds ≥ ${BONUS_RULES.minOddsPerSelection}`,
      pass: oddsOk,
      detail: oddsOk ? 'All OK' : `${badSelections.length} too low`
    });

    // 3. Stake ≤ 20
    const stake = parseFloat(totalStake) || 0;
    const stakeOk = stake > 0 && stake <= BONUS_RULES.maxStakePerBet;
    rules.push({
      rule: `Stake ≤ ETB ${BONUS_RULES.maxStakePerBet}`,
      pass: stakeOk,
      detail: stake > 0 ? `ETB ${stake}` : 'Enter stake'
    });

    return {
      allPass: rules.every(r => r.pass),
      rules
    };
  };

  // =====================================================
  // ✅ PLACE BETS — with sync lock + duplicate protection
  // =====================================================
  const placeBets = async () => {
    // ✅ SYNCHRONOUS LOCK — blocks duplicate clicks INSTANTLY
    if (placingBetRef.current) {
      console.log('⚠️ Already placing bet — blocked');
      return;
    }

    // ✅ 5-second cooldown between bets
    const now = Date.now();
    if (now - lastBetTimeRef.current < 5000) {
      console.log('⚠️ Cooldown active');
      return;
    }

    placingBetRef.current = true;
    lastBetTimeRef.current = now;
    setPlacingBet(true);

    try {
      if (betSlip.length === 0) {
        alert('Your bet slip is empty!');
        return;
      }

      if (!totalStake || totalStake <= 0) {
        alert('Please enter a stake amount');
        return;
      }

      const stake = parseFloat(totalStake) || 0;
      const totalAvailable = balance + bonusBalance;

      if (stake > totalAvailable) {
        alert(`Insufficient balance! Total available: ETB ${totalAvailable.toFixed(2)}`);
        return;
      }

      const willUseBonus = bonusBalance > 0;

      if (willUseBonus) {
        const { allPass, rules } = checkBonusRules();
        if (!allPass) {
          setRulesToShow(rules);
          setShowRulesModal(true);
          return;
        }
      }

      // ✅ Snapshot bet data BEFORE clearing
      const betSnapshot = [...betSlip];
      const stakeSnapshot = stake;
      const totalOddsSnapshot = parseFloat(calculateTotalOdds());

      // ✅ Clear bet slip IMMEDIATELY so duplicates are impossible
      setBetSlip([]);
      setTotalStake(0);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/bets/place`,
        {
          bets: betSnapshot,
          totalStake: stakeSnapshot,
          totalOdds: totalOddsSnapshot
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const ticketId = response.data.ticketId || generateTicketId();

        const betData = {
          selections: betSnapshot.map(b => ({
            match: b.match,
            market: b.market,
            betType: b.betType,
            odds: b.odds
          })),
          totalStake: stakeSnapshot,
          totalOdds: totalOddsSnapshot,
          potentialWin: stakeSnapshot * totalOddsSnapshot,
          ticketId: ticketId
        };

        printTicket(betData);
        alert('🎉 Bet placed successfully!');
        fetchBalance();
        fetchPendingMatches(); // ✅ Refresh pending match list
      } else {
        // Restore bet slip if backend rejected
        setBetSlip(betSnapshot);
        setTotalStake(stakeSnapshot);

        if (response.data.errors && response.data.errors.length > 0) {
          setRulesToShow(response.data.errors.map(msg => ({
            rule: msg, pass: false, detail: ''
          })));
          setShowRulesModal(true);
        } else {
          alert(response.data.message || 'Failed to place bets');
        }
      }
    } catch (error) {
      console.error('Error placing bets:', error);
      const errorData = error.response?.data;

      // Restore bet slip on error
      setBetSlip(betSlip);

      if (errorData?.errors && errorData.errors.length > 0) {
        setRulesToShow(errorData.errors.map(msg => ({
          rule: msg, pass: false, detail: ''
        })));
        setShowRulesModal(true);
      } else if (error.response?.status === 429) {
        alert(errorData?.message || 'Duplicate bet detected. Please wait.');
      } else {
        alert(errorData?.message || 'Failed to place bets');
      }
    } finally {
      // ✅ ALWAYS release lock
      placingBetRef.current = false;
      setPlacingBet(false);
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('betSlip');
    localStorage.removeItem('loginTimestamp');
    localStorage.removeItem('hasJoinedTelegram');
    setUser(null);
    setBalance(0);
    setBonusBalance(0);
    setPendingMatchIds([]); // ✅ Clear pending match IDs
    setShowDropdown(false);
    navigate('/');
  };

  // Handle Telegram join
  const handleJoinTelegram = () => {
    window.open('https://t.me/asharbet', '_blank');
    localStorage.setItem('hasJoinedTelegram', 'true');
    setShowTelegramPopup(false);
  };

  const handleSkipTelegram = () => {
    localStorage.setItem('hasJoinedTelegram', 'skipped');
    setShowTelegramPopup(false);
  };

  const formatCurrency = (amount) => `ETB ${parseFloat(amount || 0).toFixed(2)}`;

  const toggleMatchExpand = (matchId) => {
    setExpandedMatch(expandedMatch === matchId ? null : matchId);
  };

  const getStatusBadge = (status) => {
    if (status === 'LIVE' || status === 'live') {
      return <span className="badge-live">🔴 LIVE</span>;
    } else if (status === 'FINISHED' || status === 'finished') {
      return <span className="badge-finished">✅ Finished</span>;
    } else {
      return <span className="badge-upcoming">⏳ Upcoming</span>;
    }
  };

  const getAvailableMarkets = (match) => {
    if (!match || !match.markets) return [];
    if (typeof match.markets !== 'object' || Array.isArray(match.markets)) return [];
    
    return Object.keys(match.markets).filter(key => {
      const marketData = match.markets[key];
      return marketData && typeof marketData === 'object' && Object.keys(marketData).length > 0;
    });
  };

  const getMarketOdds = (match, marketKey) => {
    if (match.markets && match.markets[marketKey]) {
      return match.markets[marketKey];
    }
    return null;
  };

  const toggleBetSlip = () => {
    if (window.innerWidth <= 1024) {
      setShowBetSlip(!showBetSlip);
    }
  };

  const isInBetSlip = (matchId, betType, market) => {
    return betSlip.some(b => b.matchId === matchId && b.betType === betType && b.market === market);
  };

  // ===== CAROUSEL NAVIGATION (2 images per slide) =====
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // ✅ HELPER: Check if user can use Cashier features
  const canUseCashier = user && (user.role === 'cashier' || user.role === 'admin');

  // ✅ Bonus check for UI preview
  const bonusCheck = checkBonusRules();
  const willUseBonus = bonusBalance > 0 && betSlip.length > 0;

  // Bottom Navigation
  const bottomNavItems = [
    { id: 'home', label: 'HOME', icon: '🏠', path: '/' },
    { id: 'about', label: 'ABOUT', icon: 'ℹ️', path: '/about' },
    { id: 'betslip', label: 'BET SLIP', icon: '🎫', action: 'betslip' },
    { id: 'deposit', label: 'DEPOSIT', icon: '💰', path: '/deposit' },
    { id: 'my', label: 'MY', icon: '👤', path: '/MyAccount' }
  ];

  return (
    <div className="homepage-pro">
      <header className="header-pro">
        <div className="header-inner-pro">
          <div className="header-left-pro">
            <div className="logo-pro" onClick={() => navigate('/')}>
              <span className="logo-icon-pro">⚡</span>
              <span className="logo-text-pro">Ashara<span>Bet</span></span>
            </div>
            <nav className="nav-pro">
              <button className={`nav-link-pro ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>Home</button>
              <button className="nav-link-pro" onClick={() => navigate('/matches')}>Matches</button>
            </nav>
          </div>

          <div className="header-right-pro">
            <div className="live-indicator-pro">
            </div>
            {user ? (
              <>
                <div className="balance-pro">
                  <span className="balance-icon-pro">💰</span>
                  <span className="balance-value-pro">{formatCurrency(balance)}</span>
                </div>

                {/* ✅ BONUS BALANCE BADGE */}
                {bonusBalance > 0 && (
                  <div className="balance-pro bonus-balance-pro">
                    <span className="balance-icon-pro">🎁</span>
                    <span className="balance-value-pro">Bonus: {formatCurrency(bonusBalance)}</span>
                  </div>
                )}

                <button className="deposit-btn-pro" onClick={() => navigate('/deposit')}>Deposit</button>
                
                {/* ✅ CASHIER BUTTON - Only visible to cashier/admin */}
                {canUseCashier && (
                  <button 
                    className="cashier-btn-pro" 
                    onClick={() => navigate('/cashier')}
                    style={{
                      background: 'linear-gradient(135deg, #f59f00, #ff8f00)',
                      color: '#fff',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      marginLeft: '8px',
                      boxShadow: '0 2px 6px rgba(245, 159, 0, 0.4)',
                      transition: 'all 0.2s'
                    }}
                  >
                    💰 Cashier
                  </button>
                )}
                
                <div className="profile-pro" ref={dropdownRef}>
                  <button className="profile-btn-pro" onClick={() => setShowDropdown(!showDropdown)}>
                    <div className="profile-avatar-pro">{user.username?.charAt(0).toUpperCase() || 'U'}</div>
                    <span className="profile-name-pro">{user.username}</span>
                    <svg className={`profile-arrow-pro ${showDropdown ? 'rotate' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div className="dropdown-pro">
                      <div className="dropdown-items-pro">
                        <button onClick={() => navigate('/MyAccount')}>👤 My Account</button>
                        <button onClick={() => navigate('/deposit')}>💰 Deposit</button>
                        <button onClick={() => navigate('/withdraw')}>💸 Withdraw</button>
                        <button onClick={() => navigate('/bet-history')}>📊 Bet History</button>
                        
                        {/* ✅ CASHIER in dropdown */}
                        {canUseCashier && (
                          <button 
                            onClick={() => navigate('/cashier')}
                            style={{ color: '#f59f00', fontWeight: 'bold' }}
                          >
                            💰 Cashier Panel
                          </button>
                        )}
                        
                        {user.role === 'admin' && (
                          <button onClick={() => navigate('/admin/dashboard')} className="admin-link-pro">⚙️ Admin</button>
                        )}
                        <button className="logout-btn-pro" onClick={handleLogout}>🚪 Logout</button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="auth-pro">
                <button className="login-btn-pro" onClick={() => navigate('/login')}>Login</button>
                <button className="register-btn-pro" onClick={() => navigate('/register')}>Register</button>
              </div>
            )}
            <button className="mobile-toggle-pro" onClick={() => setShowMobileMenu(!showMobileMenu)}>☰</button>
          </div>
        </div>
      </header>

      {showMobileMenu && (
        <div className="mobile-overlay-pro" onClick={() => setShowMobileMenu(false)}>
          <div className="mobile-nav-pro" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-header-pro">
              <span>⚡ AsharaBet</span>
              <button className="mobile-close-pro" onClick={() => setShowMobileMenu(false)}>✕</button>
            </div>
            <div className="mobile-items-pro">
              <button className="mobile-link-pro active" onClick={() => { setActiveTab('all'); setShowMobileMenu(false); }}>🏠 Home</button>
              <button className="mobile-link-pro" onClick={() => { setActiveTab('live'); setShowMobileMenu(false); }}>🔴 Live</button>
              <button className="mobile-link-pro" onClick={() => { navigate('/matches'); setShowMobileMenu(false); }}>⚽ Matches</button>
              <button className="mobile-link-pro" onClick={() => { navigate('/deposit'); setShowMobileMenu(false); }}>💰 Deposit</button>
              <button className="mobile-link-pro" onClick={() => { navigate('/withdraw'); setShowMobileMenu(false); }}>💸 Withdraw</button>
              <button className="mobile-link-pro" onClick={() => { navigate('/aviator'); setShowMobileMenu(false); }}>✈️ Aviator</button>
              
              {/* ✅ CASHIER in mobile menu */}
              {canUseCashier && (
                <button 
                  className="mobile-link-pro" 
                  onClick={() => { navigate('/cashier'); setShowMobileMenu(false); }}
                  style={{ color: '#f59f00', fontWeight: 'bold' }}
                >
                  💰 Cashier Panel
                </button>
              )}
              
              {user?.role === 'admin' && (
                <button className="mobile-link-pro admin" onClick={() => { navigate('/admin/dashboard'); setShowMobileMenu(false); }}>⚙️ Admin</button>
              )}
              <button className="mobile-link-pro logout" onClick={() => { handleLogout(); setShowMobileMenu(false); }}>🚪 Logout</button>
            </div>
          </div>
        </div>
      )}

      <div className="main-pro">
        <div className="main-content-pro">
          {/* ✅ Sports Tabs */}
          <div className="sports-tabs-pro">
            {sports.map(sport => (
              <button
                key={sport.id}
                className={`sport-tab-pro ${selectedSport === sport.id ? 'active' : ''} ${sport.comingSoon ? 'coming-soon-tab' : ''}`}
                onClick={() => {
                  if (sport.path) {
                    navigate(sport.path);
                  } else {
                    setSelectedSport(sport.id);
                  }
                }}
              >
                {sport.icon} {sport.name}
                {sport.comingSoon && <span className="coming-soon-badge">Soon</span>}
              </button>
            ))}
          </div>

          {/* ===== CAROUSEL – 2 IMAGES AT A TIME ===== */}
          <div className="promo-carousel-container">
            <div 
              className="promo-carousel-track" 
              style={{ transform: `translateX(-${currentSlide * 50}%)` }}
            >
              {bannerImages.map((banner, index) => (
                <div key={index} className="promo-carousel-slide">
                  <img src={banner.src} alt={banner.alt} />
                </div>
              ))}
            </div>
            <div className="promo-carousel-dots">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <span
                  key={index}
                  className={`promo-carousel-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </div>
          </div>

          {/* ===== COMPACT LEAGUE FILTER ===== */}
          <div style={{ margin: '15px 0', padding: '8px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#333' }}>🏆 FILTER BY LEAGUE</span>
            </div>
            <div style={{ display: 'flex', overflowX: 'auto', gap: '6px', paddingBottom: '4px', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
              <button 
                onClick={() => setSelectedLeague('')}
                style={{
                  background: selectedLeague === '' ? '#2e7d32' : '#f0f0f0',
                  color: selectedLeague === '' ? '#fff' : '#333',
                  border: 'none', borderRadius: '15px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', flexShrink: 0
                }}
              >
                All
              </button>
              {leagues.map((league) => (
                <button 
                  key={league} 
                  onClick={() => setSelectedLeague(league)}
                  style={{
                    background: selectedLeague === league ? '#2e7d32' : '#f0f0f0',
                    color: selectedLeague === league ? '#fff' : '#333',
                    border: 'none', borderRadius: '15px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', flexShrink: 0
                  }}
                >
                  {league}
                </button>
              ))}
            </div>
          </div>

          <div className="matches-pro">
            {loading ? (
              <div className="loading-pro">
                <div className="spinner-pro"></div>
                <p>Loading matches...</p>
              </div>
            ) : comingSoonSports.includes(selectedSport) ? (
              <div className="coming-soon-container">
                <div className="coming-soon-content">
                  <div className="coming-soon-icon">
                    {selectedSport === 'FAST_KENO' && '🎲'}
                  </div>
                  <h2>Coming Soon!</h2>
                  <p>We're working hard to bring you the best Fast Keno experience.</p>
                  <p className="coming-soon-sub">Stay tuned for exciting updates!</p>
                  <div className="coming-soon-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: '65%' }}></div>
                    </div>
                    <span className="progress-text">65% Complete</span>
                  </div>
                  <button className="coming-soon-notify-btn" onClick={() => alert('🔔 You will be notified when this feature is available!')}>
                    🔔 Notify Me
                  </button>
                </div>
              </div>
            ) : groupedMatches.length > 0 ? (
              groupedMatches.map((group) => (
                <div key={group.day} className="league-group-pro">
                  <div className="league-header-pro">
                    <span className="league-icon-pro">📅</span>
                    <h3>{group.day}</h3>
                    <span className="match-count-pro">{group.matches.length} matches</span>
                  </div>

                  {group.matches.map((match) => {
                    const isExpanded = expandedMatch === match._id;
                    const availableMarkets = getAvailableMarkets(match);
                    const timeLeft = getTimeLeft(match.date);
                    const isAlreadyBet = pendingMatchIds.includes(match._id.toString()); // ✅ NEW
                    
                    return (
                      <div key={match._id} className={`match-card-pro ${isExpanded ? 'expanded' : ''}`}>
                        <div className="match-row-pro">
                          <div className="match-info-pro">
                            <div className="match-teams-pro">
                              <span className="team-home-pro" title={match.homeTeam}>{match.homeTeam}</span>
                              <span className="team-vs-pro">vs</span>
                              <span className="team-away-pro" title={match.awayTeam}>{match.awayTeam}</span>
                            </div>
                            <div className="match-meta-pro">
                              <span className="match-date-pro">{formatDate(match.date)}</span>
                              {getStatusBadge(match.status)}
                              {match.status !== 'LIVE' && match.status !== 'FINISHED' && timeLeft && (
                                <span className="time-left-badge">⏱️ {timeLeft}</span>
                              )}
                            </div>
                          </div>
                          <button className="expand-btn-pro" onClick={() => toggleMatchExpand(match._id)}>
                            {isExpanded ? '▲' : '▼'}
                            <span className="expand-count">{availableMarkets.length > 0 ? `(${availableMarkets.length})` : ''}</span>
                          </button>
                        </div>

                        {/* ✅ Already Bet indicator banner */}
                        {isAlreadyBet && (
                          <div style={{
                            background: '#fff3e0',
                            border: '1px solid #ffb74d',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            margin: '8px 0',
                            fontSize: '13px',
                            color: '#e65100',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}>
                            ✅ Already placed a bet on this match — wait for it to settle
                          </div>
                        )}

                        <div className="result-odds-pro">
                          <button 
                            className={`result-odd-btn ${isInBetSlip(match._id, '1', 'Result') ? 'selected' : ''}`}
                            onClick={() => addToBetSlip(match, '1', match.odds?.home || 'N/A', 'Result')}
                            disabled={match.status === 'FINISHED' || match.status === 'finished' || hasMatchStarted(match.date) || isAlreadyBet}
                          >
                            <span className="result-label">1</span>
                            <span className="result-odd">{match.odds?.home || 'N/A'}</span>
                          </button>
                          <button 
                            className={`result-odd-btn ${isInBetSlip(match._id, 'X', 'Result') ? 'selected' : ''}`}
                            onClick={() => addToBetSlip(match, 'X', match.odds?.draw || 'N/A', 'Result')}
                            disabled={match.status === 'FINISHED' || match.status === 'finished' || hasMatchStarted(match.date) || isAlreadyBet}
                          >
                            <span className="result-label">X</span>
                            <span className="result-odd">{match.odds?.draw || 'N/A'}</span>
                          </button>
                          <button 
                            className={`result-odd-btn ${isInBetSlip(match._id, '2', 'Result') ? 'selected' : ''}`}
                            onClick={() => addToBetSlip(match, '2', match.odds?.away || 'N/A', 'Result')}
                            disabled={match.status === 'FINISHED' || match.status === 'finished' || hasMatchStarted(match.date) || isAlreadyBet}
                          >
                            <span className="result-label">2</span>
                            <span className="result-odd">{match.odds?.away || 'N/A'}</span>
                          </button>
                        </div>

                        {isExpanded && availableMarkets.length > 0 && (
                          <div className="all-markets-pro">
                            <div className="markets-grid-pro">
                              {availableMarkets.map((marketKey) => {
                                const marketOdds = getMarketOdds(match, marketKey);
                                const marketDisplay = allMarkets[marketKey]?.label || marketKey;
                                const marketIcon = allMarkets[marketKey]?.icon || '📊';
                                
                                if (!marketOdds) return null;
                                        
                                return (
                                  <div key={marketKey} className="market-section-pro">
                                    <div className="market-header-pro">
                                      <span className="market-icon">{marketIcon}</span>
                                      <h4>{marketDisplay}</h4>
                                    </div>
                                    <div className="market-options-pro">
                                      {Object.entries(marketOdds).map(([label, odds]) => {
                                        const isSelected = isInBetSlip(match._id, label, marketDisplay);
                                        return (
                                          <button 
                                            key={label} 
                                            className={`market-option-pro ${isSelected ? 'selected' : ''}`}
                                            onClick={() => addToBetSlip(match, label, odds, marketDisplay)}
                                            disabled={isAlreadyBet}
                                          >
                                            <span className="option-label">{label}</span>
                                            <span className="option-odds">{odds}</span>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="empty-pro">
                <div className="empty-icon-pro">⚽</div>
                {selectedLeague ? (
                  <h3>No matches found for {selectedLeague}</h3>
                ) : (
                  <h3>No Upcoming Matches Available</h3>
                )}
                <p>Check back later for upcoming matches</p>
              </div>
            )}
          </div>
        </div>

        <div className={`sidebar-pro ${!showBetSlip ? 'hidden' : ''}`}>
          <div className="sidebar-header-pro">
            <h3>🎫 BET SLIP</h3>
            <span className="sidebar-count-pro">{betSlip.length}</span>
            <button className="sidebar-close-pro" onClick={toggleBetSlip}>✕</button>
          </div>

          <div className="sidebar-body-pro">
            {betSlip.length === 0 ? (
              user ? (
                <div className="telegram-join-betslip">
                  <div className="telegram-join-icon">📢</div>
                  <h4>Join Our Telegram Channel!</h4>
                  <p>Get live updates, promotions &amp; betting tips</p>
                  <button className="telegram-join-btn-betslip" onClick={handleJoinTelegram}>
                    📱 Join Telegram
                  </button>
                  <button className="telegram-skip-btn-betslip" onClick={handleSkipTelegram}>
                    Maybe Later
                  </button>
                </div>
              ) : (
                <div className="empty-betslip-pro">
                  <div className="empty-icon-betslip-pro">📋</div>
                  <p>No selections</p>
                  <span>Click odds to add</span>
                </div>
              )
            ) : (
              <>
                <div className="betslip-list-pro">
                  {betSlip.map((bet, index) => {
                    const oddsTooLow = Number(bet.odds) < BONUS_RULES.minOddsPerSelection;
                    return (
                      <div key={index} className={`betslip-item-pro ${oddsTooLow && willUseBonus ? 'betslip-item-warning' : ''}`}>
                        <div className="betslip-header-pro">
                          <div className="betslip-match-pro">
                            <p className="betslip-match-name-pro">{bet.match}</p>
                            <span className="betslip-league-pro">{bet.league}</span>
                            <span className="betslip-market-pro">{bet.market}</span>
                          </div>
                          <button className="betslip-remove-pro" onClick={() => removeFromBetSlip(index)}>✕</button>
                        </div>
                        <div className="betslip-body-pro">
                          <span className="betslip-selection-pro">{bet.betType}    ..................... {bet.odds}  odd</span>
                          {oddsTooLow && willUseBonus && (
                            <span className="betslip-odds-warning">⚠️ Odds below {BONUS_RULES.minOddsPerSelection}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="betslip-total-stake-pro">
                  <label>Total Stake</label>
                  <input
                    type="number"
                    className="betslip-stake-input-pro"
                    placeholder="Enter total stake"
                    value={totalStake || ''}
                    onChange={(e) => updateTotalStake(e.target.value)}
                    min="0"
                    step="0.01"
                  />
                </div>

                {/* ✅ BONUS RULES PREVIEW */}
                {willUseBonus && (
                  <div className="bonus-rules-panel">
                    <div className="bonus-rules-header">
                      🎁 <strong>Bonus Bet Rules</strong>
                      <span className="bonus-amount-badge">ETB {bonusBalance.toFixed(2)}</span>
                    </div>
                    <div className="bonus-rules-list">
                      {bonusCheck.rules.map((r, i) => (
                        <div key={i} className={`bonus-rule-row ${r.pass ? 'pass' : 'fail'}`}>
                          <span className="bonus-rule-icon">{r.pass ? '✅' : '❌'}</span>
                          <span className="bonus-rule-text">{r.rule}</span>
                          <span className="bonus-rule-detail">{r.detail}</span>
                        </div>
                      ))}
                    </div>
                    {bonusCheck.allPass ? (
                      <div className="bonus-rules-status ok">✅ All rules satisfied</div>
                    ) : (
                      <div className="bonus-rules-status warn">⚠️ Rules not met — click Place Bet for details</div>
                    )}
                  </div>
                )}

                <div className="betslip-summary-pro">
                  <div className="summary-row-pro">
                    <span>Total Stake</span>
                    <strong>{formatCurrency(parseFloat(totalStake) || 0)}</strong>
                  </div>
                  <div className="summary-row-pro">
                    <span>Total Odds</span>
                    <strong>{calculateTotalOdds()}</strong>
                  </div>
                  <div className="summary-row-pro potential">
                    <span>Potential Win</span>
                    <strong>{formatCurrency(parseFloat(calculatePotentialWinnings()) || 0)}</strong>
                  </div>
                </div>

                {/* ✅ PLACE BET — shows loading state */}
                <button 
                  className="place-bet-pro" 
                  onClick={placeBets}
                  disabled={placingBet}
                  style={placingBet ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  {placingBet ? '⏳ Placing bet...' : 'Place Bet'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {isMobile && (
        <div className="bottom-nav">
          {bottomNavItems.map((item) => (
            <button
              key={item.id}
              className="bottom-nav-item"
              onClick={() => {
                if (item.action === 'betslip') {
                  toggleBetSlip();
                } else if (item.path) {
                  navigate(item.path);
                }
              }}
            >
              <span className="bottom-nav-icon">{item.icon}</span>
              <span className="bottom-nav-label">{item.label}</span>
              {item.id === 'betslip' && betSlip.length > 0 && (
                <span className="bottom-nav-badge">{betSlip.length}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {betSlip.length > 0 && !showBetSlip && isMobile && (
        <button className="mobile-betslip-pro" onClick={toggleBetSlip}>
          🎫 {betSlip.length}
        </button>
      )}

      {/* ✅ BONUS RULES MODAL */}
      {showRulesModal && (
        <div className="rules-modal-overlay" onClick={() => setShowRulesModal(false)}>
          <div className="rules-modal" onClick={e => e.stopPropagation()}>
            <div className="rules-modal-header">
              <h3>🎁 Bonus Bet Rules Not Satisfied</h3>
              <button className="rules-modal-close" onClick={() => setShowRulesModal(false)}>✕</button>
            </div>
            <p className="rules-modal-subtitle">
              Your bet slip uses <strong>bonus balance</strong>. The following rules must be met:
            </p>
            <div className="rules-modal-list">
              {rulesToShow.map((r, i) => (
                <div key={i} className={`rules-modal-row ${r.pass ? 'pass' : 'fail'}`}>
                  <span className="rules-modal-icon">{r.pass ? '✅' : '❌'}</span>
                  <span className="rules-modal-text">{r.rule}</span>
                  {r.detail && <span className="rules-modal-detail">{r.detail}</span>}
                </div>
              ))}
            </div>
            <div className="rules-modal-hint">
              <strong>💡 Tips:</strong>
              <ul>
                <li>Add more selections to reach {BONUS_RULES.minSelections} total</li>
                <li>Ensure each selection has odds ≥ {BONUS_RULES.minOddsPerSelection}</li>
                <li>Keep stake at or below ETB {BONUS_RULES.maxStakePerBet}</li>
                <li>Or use only real balance (deposit more) to bypass these rules</li>
              </ul>
            </div>
            <button className="rules-modal-btn" onClick={() => setShowRulesModal(false)}>Got it</button>
          </div>
        </div>
      )}

      {showTelegramPopup && (
        <div className="telegram-popup-overlay" onClick={handleSkipTelegram}>
          <div className="telegram-popup-simple" onClick={(e) => e.stopPropagation()}>
            <div className="telegram-popup-icon-simple">📢</div>
            <h2>Join Our Telegram Channel!</h2>
            <p>Stay updated with live odds, promotions, and exclusive betting tips!</p>
            <div className="telegram-buttons">
              <button className="telegram-join-btn-simple" onClick={handleJoinTelegram}>
                📱 Join Now
              </button>
              <button className="telegram-skip-btn-simple" onClick={handleSkipTelegram}>
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;